import type { StylesConfig, GroupBase } from 'react-select';

/**
 * Theme-aware styles for react-select that use Bootstrap CSS variables.
 * Adapts automatically to light/dark mode via data-bs-theme.
 *
 * Usage:
 *   import { reactSelectStyles } from '../utils/reactSelectTheme';
 *   <Select styles={reactSelectStyles} ... />
 *
 * To merge with component-specific overrides:
 *   <Select styles={{ ...reactSelectStyles, container: (base) => ({ ...base, minWidth: '250px' }) }} />
 */
export const reactSelectStyles: StylesConfig<any, any, GroupBase<any>> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: 'var(--bs-body-bg)',
    borderColor: state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25)' : undefined,
    '&:hover': {
      borderColor: 'var(--bs-primary)',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--bs-body-bg)',
    borderColor: 'var(--bs-border-color)',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--bs-primary)'
      : state.isFocused
        ? 'var(--bs-tertiary-bg)'
        : 'transparent',
    color: state.isSelected ? '#fff' : 'var(--bs-body-color)',
    '&:active': {
      backgroundColor: 'var(--bs-primary)',
      color: '#fff',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--bs-body-color)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--bs-body-color)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--bs-secondary-color)',
    whiteSpace: 'nowrap' as const,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--bs-tertiary-bg)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--bs-body-color)',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--bs-body-color)',
    '&:hover': {
      backgroundColor: 'var(--bs-danger)',
      color: '#fff',
    },
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'var(--bs-border-color)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--bs-secondary-color)',
    '&:hover': {
      color: 'var(--bs-body-color)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--bs-secondary-color)',
    '&:hover': {
      color: 'var(--bs-danger)',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--bs-secondary-color)',
  }),
};
